import moment from 'moment-timezone';


const SERVER_TIMEZONE: string = process.env.REACT_APP_SERVER_TIMEZONE || 'UTC';

/**
 * Format an ISO date string received from backend to the raw DB date.
 * When the DB stores a date as Date data type, there is no timezone attached.
 * When backend retrieves the date value, it converts to a javascript Date object.
 * Javascript Date object has a time component to it.
 * In this case, it will be DB date with the time <code>00:00:00</code> of the backend server timezone.
 * So if server timezone is +08:00 and DB date is <code>2025-06-01</code>, it will be <code>2025-06-01T00:00:00+08:00</code>
 * When backend sends the javascript date, it serialises the date to ISO string as <code>2025-05-31T16:00:00:00Z</code>
 * This method ensures that the original date in the DB, <code>2025-06-01</code> is returned
 * The timezone of the backend API server needs to be configured in the environment variable <strong>REACT_APP_SERVER_TIMEZONE</strong>
 * If not configured, <strong>UTC</strong> is assumed
 * @param isoDate - ISO string or JS Date from backend
 * @param format - Optional format string (default: 'YYYY-MM-DD')
 * @returns Formatted date matching DB, or empty string if input is invalid
 */
export function formatDbDate(
  isoDate: string | Date | null | undefined,
  format: string = 'YYYY-MM-DD'
): string {
  if (!isoDate) return '';

  const m = moment.tz(isoDate, SERVER_TIMEZONE);
  if (!m.isValid()) {
    console.warn('Invalid date passed to formatDbDate:', isoDate);
    return '';
  }

  return m.format(format);
}

/**
* Format an ISO date string based on ISO_8601 (e.g. "2025-03-15", "2025-03-15T12:30:00Z", "2025-03-15T12:30:00+02:00") according to input format.
* @param isoDate - ISO string
* @param format - Optional format string (default: 'DD/MMM/DD')
* @returns Formatted date matching input format, or null if input is invalid
*/
export function formatIsoDate(
  isoDate: string | null,
  format: string = 'DD/MMM/YYYY'): string | null {

  if(isoDate === null || !isoDate)
    return null;

  const m = moment(isoDate,moment.ISO_8601);
  if (!m.isValid()) {
    console.warn('Invalid date passed to isoDate:', isoDate);
    return null;
  }

  return m.format(format);
}

export interface CalendarInput {
  firstDay: 0 | 1 | 2 | 3 | 4 | 5 | 6 ;
  eventTimeInput: {
    hour: '2-digit',
    minute: '2-digit',
    hour12: boolean,
  };
}

export interface LocaleTimeCalendarInput {
  timeFormat: string;
  calendarInput: CalendarInput;
}

const TIME_FORMAT_12H = 'hh:mm A';
const TIME_FORMAT_24H = 'HH:mm';

const localeTimeCalendarInputMap:  { [key: string]: LocaleTimeCalendarInput } = {
  "en-au": {
    timeFormat: TIME_FORMAT_12H,
    calendarInput:{ firstDay: 1, eventTimeInput:{ hour: '2-digit', minute: '2-digit', hour12: true} }
  },
  "en-gb": {
    timeFormat: TIME_FORMAT_24H,
    calendarInput:{ firstDay: 1, eventTimeInput:{ hour: '2-digit', minute: '2-digit', hour12: false} }
  },
  "en-nz": {
    timeFormat: TIME_FORMAT_12H,
    calendarInput:{ firstDay: 1, eventTimeInput:{ hour: '2-digit', minute: '2-digit', hour12: true} }
  },
  "en-sg": {
    timeFormat: TIME_FORMAT_24H,
    calendarInput:{ firstDay: 1, eventTimeInput:{ hour: '2-digit', minute: '2-digit', hour12: false} }
  },
  "en": {
    timeFormat: TIME_FORMAT_12H,
    calendarInput:{ firstDay: 0, eventTimeInput:{ hour: '2-digit', minute: '2-digit', hour12: true} }
  },
}


/**
 * @param {string} inputTime
 * @param {string} inputFormat
 * @param {string} locale
 */
export function getMomentLocaleTimeFormat(inputTime: string, inputFormat: string, locale: string): string {

  const _locale = locale || "en";

  const {timeFormat} = localeTimeCalendarInputMap[_locale];

  return moment(inputTime, inputFormat).format(timeFormat);
}



/**
 * @param {string} locale
 */
export function getCalendarInput(locale: string)  {

  const _locale = locale || "en";
  const { calendarInput } = localeTimeCalendarInputMap[_locale];

  return calendarInput;
}

/**
 * @param {string} locale
 */
export function getTimeFormat(locale: string) {

  const _locale = locale || "en";
  const { timeFormat }= localeTimeCalendarInputMap[_locale];

  return timeFormat;
}
